import { useEffect } from 'react'
import type { RefObject } from 'react'

const TRANSITION = 'opacity 0.7s ease-out, transform 0.7s cubic-bezier(0.2,0.7,0.2,1)'
const MAX_STAGGER_STEPS = 5
const STAGGER_STEP_MS = 70
const FAILSAFE_MS = 2500

/**
 * Reproduz o reveal-on-scroll de `docs/design/prototype/support.js` (mountReveal): elementos
 * `[data-reveal]` entram com fade + translate quando cruzam a viewport, com atraso escalonado
 * pela posicao entre os irmaos (ate 5 * 70ms) e failsafe de 2.5s caso o IntersectionObserver
 * nunca dispare. Sob prefers-reduced-motion, tudo aparece imediatamente, sem observer.
 */
export function useRevealOnScroll(containerRef: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const container = containerRef.current
    if (container === null) {
      return
    }

    const elements = Array.from(container.querySelectorAll<HTMLElement>('[data-reveal]'))
    if (elements.length === 0) {
      return
    }

    // jsdom (ambiente de teste) nao implementa matchMedia nem IntersectionObserver: nesses
    // casos tratamos como "sem observer disponivel" e revelamos tudo de imediato, em vez de
    // lancar uma excecao ao montar a pagina.
    const reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce || !('IntersectionObserver' in window)) {
      elements.forEach((el) => {
        el.style.opacity = '1'
        el.style.transform = 'none'
      })
      return
    }

    elements.forEach((el) => {
      el.style.opacity = '0'
      el.style.transform = 'translate3d(0,20px,0)'
      el.style.transition = TRANSITION
    })

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return
          }

          const el = entry.target
          if (!(el instanceof HTMLElement)) {
            return
          }

          const siblings = Array.from(el.parentElement?.children ?? [])
          const delay = Math.min(siblings.indexOf(el), MAX_STAGGER_STEPS) * STAGGER_STEP_MS
          window.setTimeout(() => {
            el.style.opacity = '1'
            el.style.transform = 'none'
          }, delay)
          observer.unobserve(el)
        })
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.12 },
    )

    elements.forEach((el) => {
      observer.observe(el)
    })

    const failsafe = window.setTimeout(() => {
      elements.forEach((el) => {
        el.style.opacity = '1'
        el.style.transform = 'none'
      })
      observer.disconnect()
    }, FAILSAFE_MS)

    return () => {
      observer.disconnect()
      window.clearTimeout(failsafe)
    }
  }, [containerRef])
}
